import React from 'react';
import { Link } from 'gatsby';
import styled from "@emotion/styled";
import colors from "styles/colors";
import dimensions from 'styles/dimensions';
import { Rubric } from 'styles/typography';
import RichText from 'components/RichText';
import Checkmark from '../../../../vectors/checkmark.svg';
import Button from 'components/_ui/Button';

const CardContainer = styled.div`
  position: relative;
  background-color: #fff;
  padding: 2.4rem;
  width: 100%;
  
  header {
    text-align: center;
    
    & + * {
      margin-top: 2.4rem;
    }
  }
  
  @media(min-width: ${dimensions.tabletLandscapeUp}px) {
    display: flex;
    flex-direction: column;
    flex: 1 1 100%;
    height: 100%;
  }
  
  @media(min-width: ${dimensions.desktopUp}px) {
    padding: 3.2rem;
  }
`;

const CardIllustration = styled.div`
  width: 100%;
  max-width: 16.4rem;
  margin-left: auto;
  margin-right: auto;
  user-select: none;
  pointer-events: none;
  
  & + * {
    margin-top: 3.2rem;
  }
  
  img {
    width: 100%;
  }
`;

const CardTitle = styled.div`
  text-align: center;
`;

const CardLogo = styled.img`
  display: block;
  margin-left: auto;
  margin-right: auto;
  user-select: none;
  pointer-events: none;
  
  & + * {
    margin-top: 1.6rem;
  }
`;

const CardLogline = styled.span`
  display: block;
  color: ${colors.teal800};
  font-size: 1.4rem;
  font-weight: 500;
`;


const CardAudience = styled.ul`
  max-width: 19.2rem;
  margin: 0 auto;
  
  &::before {
    content: 'Includes';
    display: block;
    font-size: 1.2rem;
    font-weight: 500;
    color: ${colors.teal700};
    text-align: center;
    margin-bottom: 1.6rem;
  }
  
  li {
    position: relative;
    
    p {
      font-size: 1.4rem;
    }
    
    & + * {
      margin-top: 1.6rem;
    }
  }
  
  svg {
    width: 1.1rem;
    height: 1rem;
    position: absolute;
    right: calc(100% + 1.2rem);
    top: 0.4rem;
    fill: ${colors.green400};
  }
`;

const CardButton = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  margin-top: 3.2rem;
  flex: 2 1 100%;
`;

const CardPrice = styled.div`
  text-align: center;
  margin-top: 1.6rem;
  min-height: 3.2rem;
  
  p {
    font-size: 1.4rem;
    
    &:first-of-type {
      font-weight: 500;
    }
    
    & + * {
      margin-top: 0;
    }
  }
`;

const CardComparison = ({ plan, product, destination }) => {
  return (
    <CardContainer>
      <header>
        {plan && plan.feature_image && (
          <CardIllustration>
            <img
              src={plan.feature_image.url}
              alt={plan.display_title}
              loading="lazy"
            />
          </CardIllustration>
        )}

        {product && product.preview_image && (
          <CardIllustration>
            <img
              src={product.preview_image.url}
              alt={product.name}
              loading="lazy"
            />
          </CardIllustration>
        )}

        {product && product.logo && (
          <CardLogo
            src={product.logo.url}
            alt={product.name}
          />
        )}

        {plan && plan.display_title && (
          <CardTitle>
            <Rubric>{plan.display_title}</Rubric>
          </CardTitle>
        )}

        {product && product.logline && <CardLogline>{product.logline}</CardLogline>}
        {plan && plan.short_description && <CardLogline>{plan.short_description}</CardLogline>}
      </header>

      <CardAudience>
        {plan && plan.traits.map((trait, i) => (
          <li key={i}>
            <Checkmark/>

            <span>
              <RichText render={trait.content}/>
            </span>
          </li>
        ))}

        {product && product.audience_traits.map((trait, i) => (
          <li key={i}>
            <Checkmark/>
            <span>{trait.text}</span>
          </li>
        ))}
      </CardAudience>

      <CardButton>
        <Button
          as={Link}
          to={(plan && plan.cta_destination) || destination || '/contact-us'}
          className='Button--tertiary'
        >{(plan && plan.cta_text) || 'Get Started'}</Button>
      </CardButton>

      {(plan && (plan.price || plan.price_subtext)) && (
        <CardPrice>
          {plan.price && <p>{plan.price}</p>}
          {plan.price_subtext && <p>{plan.price_subtext}</p>}
        </CardPrice>
      )}
    </CardContainer>
  );
};

export default CardComparison;
